import React, { useState } from "react";
import { useNavigate, Link, Navigate, useSearchParams } from "react-router-dom";
import { Leaf, ArrowRight, Mail, Lock, User, Building2 } from "lucide-react";
import { useAuth, formatApiError } from "./auth";
import { useI18n } from "./i18n";
import LangToggle from "./components/LangToggle";
import StandardsLogos from "./components/StandardsLogos";
import ConsentCheckboxes from "./components/ConsentCheckboxes";
import { pwValid } from "./api";

// Light theme inputs — cream bg with green focus
const inputCls =
  "w-full rounded-xl border border-[#e2e5d8] bg-white px-11 py-3 text-[15px] text-[#1b2420] outline-none transition-all placeholder:text-[#9ba394] focus:border-[#14512f] focus:ring-4 focus:ring-[#14512f]/8";

const iconInputWrap = "relative";
const iconCls = "pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7a8175]";

export function AuthShell({ title, subtitle, children, footer }) {
  const { t } = useI18n();
  return (
    <div className="relative min-h-screen overflow-hidden bg-[#FAFAF5] text-[#1b2420]">
      {/* Warm cream backdrop with subtle green wash */}
      <div className="pointer-events-none absolute inset-0"
           style={{ backgroundImage: "radial-gradient(1000px 500px at 15% -10%, rgba(20,81,47,0.06), transparent 60%), radial-gradient(900px 500px at 100% 100%, rgba(212,248,112,0.14), transparent 55%)" }} />

      {/* Language toggle top-right */}
      <div className="absolute right-4 top-4 z-30 sm:right-6 sm:top-6">
        <LangToggle />
      </div>

      <div className="relative z-10 mx-auto flex min-h-screen max-w-6xl flex-col items-stretch px-6 py-10 lg:flex-row lg:items-center lg:gap-16">

        {/* Left — brand & value prop (desktop) */}
        <div className="hidden lg:flex lg:w-1/2 lg:flex-col lg:justify-between lg:py-8">
          <Link to="/" className="inline-flex items-center gap-2.5" data-testid="auth-brand-link">
            <span className="grid h-10 w-10 place-items-center rounded-xl bg-[#14512f] text-[#D4F870] shadow-sm">
              <Leaf className="h-5 w-5" strokeWidth={2.5} />
            </span>
            <span className="font-display text-xl tracking-tight text-[#14512f]">
              Enasuspro<sup className="text-[10px] text-[#6b7268]">®</sup> Carbon Ledger
            </span>
          </Link>

          <div className="my-12">
            <p className="mb-4 inline-flex items-center gap-2 rounded-full border border-[#14512f]/15 bg-white/70 px-3 py-1 text-[11px] font-medium uppercase tracking-widest text-[#14512f]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#14512f]" />
              ISO 14064 · CBAM · IFRS S1/S2
            </p>
            <h2 className="font-display text-4xl leading-[1.05] tracking-tight text-[#16321f] sm:text-5xl" data-testid="auth-hero-title">
              {t("authHeroTitle")}
            </h2>
            <p className="mt-5 max-w-md text-[15px] leading-relaxed text-[#5c6259]">
              {t("authHeroDesc")}
            </p>
          </div>

          <div className="flex items-center justify-between text-xs text-[#7a8175]">
            <span>© {new Date().getFullYear()} Enasuspro<sup>®</sup></span>
            <Link to="/pricing" className="hover:text-[#14512f] transition-colors" data-testid="auth-pricing-link-desktop">
              {t("viewPricing")}
            </Link>
          </div>
        </div>

        {/* Right — form card */}
        <div className="flex w-full flex-1 items-center justify-center lg:w-1/2">
          <div className="w-full max-w-md">

            {/* Mobile brand */}
            <Link to="/" className="mb-8 inline-flex items-center gap-2.5 lg:hidden" data-testid="auth-brand-link-mobile">
              <span className="grid h-9 w-9 place-items-center rounded-lg bg-[#14512f] text-[#D4F870]">
                <Leaf className="h-4 w-4" strokeWidth={2.5} />
              </span>
              <span className="font-display text-lg tracking-tight text-[#14512f]">Enasuspro<sup className="text-[9px] text-[#6b7268]">®</sup> Carbon Ledger</span>
            </Link>

            <div className="rounded-2xl border border-[#e4e6dd] bg-white p-7 sm:p-9"
                 style={{ boxShadow: "0 30px 60px -20px rgba(20,81,47,0.14), 0 1px 0 rgba(255,255,255,0.6) inset" }}
                 data-testid="auth-card">
              <h1 className="font-display text-3xl leading-tight tracking-tight text-[#16321f] sm:text-4xl" data-testid="auth-title">{title}</h1>
              <p className="mt-2 text-[14px] text-[#6b7268]" data-testid="auth-subtitle">{subtitle}</p>

              <div className="mt-7">
                {children}
              </div>

              <div className="mt-6 border-t border-[#eef0e7] pt-5 text-center text-[13px] text-[#6b7268]" data-testid="auth-footer">
                {footer}
              </div>
            </div>

            <div className="mt-5 flex flex-col items-center gap-4">
              <Link to="/pricing" className="text-[13px] text-[#6b7268] hover:text-[#14512f] transition-colors lg:hidden" data-testid="auth-pricing-link">
                {t("viewPricing")}
              </Link>
              <div className="flex items-center justify-center gap-2 text-[10px] font-medium uppercase tracking-[0.16em] text-[#7a8175]">
                <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-[#14512f] text-white" data-testid="trust-shield">
                  <svg width="10" height="10" viewBox="0 0 12 12" aria-hidden="true">
                    <path d="M6 1 L10 2.5 L10 6.5 C10 8.7 8.2 10.4 6 11 C3.8 10.4 2 8.7 2 6.5 L2 2.5 Z" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
                    <path d="M4.2 6 L5.3 7.1 L7.8 4.6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                  </svg>
                </span>
                <span data-testid="trust-badge-text">{t("trustBadge")}</span>
              </div>
              <div className="w-full">
                <div className="mb-3 text-center text-[9px] font-medium uppercase tracking-[0.20em] text-[#8a8f83]">
                  {t("standardsAligned")}
                </div>
                <StandardsLogos />
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}

export function Login() {
  const { login } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const initialSegment = params.get("segment") === "enterprise" ? "enterprise" : "sme";
  const [segment, setSegment] = useState(initialSegment);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try { await login(email, password); nav("/"); }
    catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || e2.message); }
    finally { setLoading(false); }
  };

  const segBtn = (val, label, testid) => (
    <button type="button" onClick={() => setSegment(val)} data-testid={testid}
      className={`rounded-xl border px-3 py-2.5 text-[13px] font-medium transition-all ${
        segment === val
          ? "border-[#14512f] bg-[#eef6ef] text-[#14512f] shadow-sm"
          : "border-[#dcdfd4] bg-white text-[#6b7268] hover:border-[#b8bfaa] hover:text-[#14512f]"
      }`}>
      {label}
    </button>
  );

  return (
    <AuthShell title={t("authTitleLogin")} subtitle={t("authSubtitleLogin")}
      footer={<>{t("authNoAccount")} <Link to={`/register?role=organization&segment=${segment}`} className="font-medium text-[#14512f] hover:underline" data-testid="switch-to-register">{t("authSignUp")}</Link></>}>
      <form onSubmit={submit} className="space-y-3.5" data-testid="login-form">
        {err && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-[13px] text-red-700" data-testid="login-error">{err}</div>}
        <div className="grid grid-cols-2 gap-2.5">
          {segBtn("sme", "İhracatçı KOBİ", "login-segment-sme")}
          {segBtn("enterprise", "Kurumsal", "login-segment-enterprise")}
        </div>
        <div className={iconInputWrap}>
          <Mail className={iconCls} />
          <input className={inputCls} type="email" placeholder={t("authEmail")} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" autoComplete="email" />
        </div>
        <div className={iconInputWrap}>
          <Lock className={iconCls} />
          <input className={inputCls} type="password" placeholder={t("authPassword")} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" autoComplete="current-password" />
        </div>
        <div className="-mt-1 text-right">
          <Link to="/forgot-password" className="text-[13px] font-medium text-[#14512f] hover:underline" data-testid="forgot-password-link">{t("forgotLink")}</Link>
        </div>
        <button type="submit" disabled={loading} data-testid="login-submit"
          className="mt-1.5 flex w-full items-center justify-center gap-2 rounded-xl bg-[#14512f] px-4 py-3 text-[14px] font-semibold text-white transition-all hover:bg-[#0f4526] active:scale-[0.99] disabled:opacity-60"
          style={{ boxShadow: "0 8px 20px -6px rgba(20,81,47,0.35)" }}>
          {loading ? "..." : t("authTitleLogin")} <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
        </button>
      </form>
    </AuthShell>
  );
}

export function Register() {
  const { register } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const paramRole = params.get("role");
  const isSupplier = paramRole === "supplier";
  const initialRole = ["consultant", "supplier"].includes(paramRole) ? paramRole : "organization";
  const [form, setForm] = useState({ name: "", email: params.get("email") || "", password: "", role: initialRole, org_name: "" });
  const [consent, setConsent] = useState({ kvkk: false, etk: false });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const up = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (!pwValid(form.password)) { setErr(t("pwRule")); return; }
    setLoading(true);
    try {
      await register({ ...form, kvkk_consent: consent.kvkk, etk_consent: consent.etk });
      nav("/");
    }
    catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || e2.message); }
    finally { setLoading(false); }
  };

  const roleBtn = (val, label, testid) => (
    <button type="button" onClick={() => setForm((p) => ({ ...p, role: val }))} data-testid={testid}
      className={`rounded-xl border px-3 py-2.5 text-[13px] font-medium transition-all ${
        form.role === val
          ? "border-[#14512f] bg-[#eef6ef] text-[#14512f] shadow-sm"
          : "border-[#dcdfd4] bg-white text-[#6b7268] hover:border-[#b8bfaa] hover:text-[#14512f]"
      }`}>
      {label}
    </button>
  );

  return (
    <AuthShell title={t("authTitleRegister")} subtitle={t("authSubtitleRegister")}
      footer={<>{t("authHaveAccount")} <Link to="/login" className="font-medium text-[#14512f] hover:underline" data-testid="switch-to-login">{t("authSignIn")}</Link></>}>
      <form onSubmit={submit} className="space-y-3.5" data-testid="register-form">
        {err && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-[13px] text-red-700" data-testid="register-error">{err}</div>}

        {isSupplier ? (
          <div className="rounded-xl border border-[#14512f] bg-[#eef6ef] px-3 py-2.5 text-[13px] text-[#14512f]" data-testid="role-supplier">
            <div className="font-medium">{t("authRoleSupplier")}</div>
            <div className="text-xs text-[#4b544c]">{t("supplierRegisterHint")}</div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2.5">
            {roleBtn("organization", t("authRoleOrg"), "role-organization")}
            {roleBtn("consultant", t("authRoleConsultant"), "role-consultant")}
          </div>
        )}

        <div className={iconInputWrap}>
          <User className={iconCls} />
          <input className={inputCls} placeholder={t("authName")} value={form.name} onChange={up("name")} data-testid="register-name" autoComplete="name" />
        </div>
        <div className={iconInputWrap}>
          <Building2 className={iconCls} />
          <input className={inputCls} placeholder={form.role === "consultant" ? t("authConsultingFirm") : t("authOrg")} value={form.org_name} onChange={up("org_name")} data-testid="register-org" autoComplete="organization" />
        </div>
        <div className={iconInputWrap}>
          <Mail className={iconCls} />
          <input className={inputCls} type="email" placeholder={t("authEmail")} value={form.email} onChange={up("email")} data-testid="register-email" autoComplete="email" />
        </div>
        <div className={iconInputWrap}>
          <Lock className={iconCls} />
          <input className={inputCls} type="password" placeholder={t("authPassword")} value={form.password} onChange={up("password")} data-testid="register-password" autoComplete="new-password" />
        </div>
        <p className={`-mt-1.5 text-xs ${form.password && !pwValid(form.password) ? "text-red-600" : "text-[#8a8f83]"}`} data-testid="register-password-hint">{t("pwRule")}</p>

        <div className="rounded-lg border border-[#eef0e7] bg-[#f8faf3] px-3 py-3">
          <ConsentCheckboxes
            kvkk={consent.kvkk}
            etk={consent.etk}
            onChange={setConsent}
            testIdPrefix="register"
          />
        </div>

        <button type="submit" disabled={loading || !consent.kvkk} data-testid="register-submit"
          className="mt-1.5 flex w-full items-center justify-center gap-2 rounded-xl bg-[#14512f] px-4 py-3 text-[14px] font-semibold text-white transition-all hover:bg-[#0f4526] active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50"
          style={{ boxShadow: "0 8px 20px -6px rgba(20,81,47,0.35)" }}>
          {loading ? "..." : t("authCreateAccount")} <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
        </button>
      </form>
    </AuthShell>
  );
}

export function ProtectedRoute({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return <div className="grid min-h-screen place-items-center bg-[#FAFAF5] text-sm text-[#6b7268]">…</div>;
  }
  if (user === false) {
    return <Navigate to="/login" replace />;
  }
  return children;
}
